/**
 * As ações de uma saída do card "Preparação do pacote" (E168.F7, GUS-180): baixar a prévia (ou o
 * final, E169.F2) e retomar só a saída que falhou. Saiu de `LinhaDoPacote` na E172.F19.
 *
 * E172.F19 (GUS-230 item 5): enquanto a preparação refaz a saída (depois da conferência do portal,
 * da revisão ou da análise facial), o botão fica desabilitado com "Atualizando…" e o aviso de que a
 * prévia anterior não vale para a análise nova. O card lido antes da renovação ainda oferece a
 * prévia: o download então recebe a recusa `atualizando`, mostra o mesmo aviso e pede o card de
 * novo (`onAtualizar`), que passa a esperar a prévia nova. Nunca o PDF anterior sem aviso.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import { CorrigirNaMesa } from '@/components/DocumentoDaMesa/CorrigirNaMesa';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { salvarArquivoBaixado } from '@/lib/http_download';
import {
  baixarPreviaDoPacote,
  emAtualizacao,
  PacoteDoCasoError,
  type LinhaDoPacoteData,
  type SaidaDoPacote,
} from '@/lib/preparacao_do_pacote';
import type { DesfechoDaRetomada } from '@/lib/usePreparacaoDoPacote';

import { RetomadaDaSaida } from './RetomadaDaSaida';

const t = ptBR.preparacaoDoPacote;

type FalhaDoDownload = 'erro' | 'atualizando' | null;

function AvisoDeAtualizacao({ saida }: { saida: SaidaDoPacote }): JSX.Element {
  return (
    <p
      role="status"
      className="text-body-sm text-accent-700"
      data-testid={`pacote-atualizando-${saida}`}
    >
      {t.avisoDeAtualizacao}
    </p>
  );
}

/** A saída que a preparação está refazendo: o botão espera, com o porquê. */
function EmAtualizacao({ saida }: { saida: SaidaDoPacote }): JSX.Element {
  return (
    <div className="space-y-1">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled
        data-testid={`pacote-baixar-${saida}`}
      >
        {t.atualizando}
      </Button>
      <AvisoDeAtualizacao saida={saida} />
    </div>
  );
}

export interface AcoesDaSaidaProps {
  client: AxiosInstance;
  caseId: string;
  saida: SaidaDoPacote;
  linha: LinhaDoPacoteData;
  onRetomar: (saida: SaidaDoPacote, justificativa: string) => Promise<DesfechoDaRetomada>;
  /** E172.F19: o download encontrou a prévia em atualização; o card é lido de novo. */
  onAtualizar?: (() => void) | undefined;
}

type BaixarProps = Omit<AcoesDaSaidaProps, 'onRetomar'>;

/** O arquivo guardado para baixar: a prévia (ou o final), com a recusa dita na própria linha. */
function BaixarASaida({ client, caseId, saida, linha, onAtualizar }: BaixarProps): JSX.Element {
  const [baixando, setBaixando] = useState(false);
  const [falha, setFalha] = useState<FalhaDoDownload>(null);

  async function baixar(): Promise<void> {
    setBaixando(true);
    setFalha(null);
    try {
      salvarArquivoBaixado(await baixarPreviaDoPacote(client, caseId, saida));
    } catch (err) {
      const atualizando = err instanceof PacoteDoCasoError && err.motivo === 'atualizando';
      setFalha(atualizando ? 'atualizando' : 'erro');
      if (atualizando) onAtualizar?.();
    } finally {
      setBaixando(false);
    }
  }

  return (
    <div className="space-y-1">
      {saida === 'exame' && !linha.final && linha.jornada !== 'revisado' ? (
        <p className="text-caption text-fg-dim">{t.previa}</p>
      ) : null}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={baixando}
        onClick={() => void baixar()}
        data-testid={`pacote-baixar-${saida}`}
      >
        {baixando ? t.baixando : linha.final ? t.baixarFinal : t.consultar}
      </Button>
      {/* E196.F1 (GUS-260) → E203.F5.T05: o ZIP do exame com os arquivos saiu do card; fica ao
          lado de "Baixar exame", no alto do exame da mesa (a conferência final não muda). */}
      {/* E171.F3 (ADR-0122 §2.7): a redação da prévia se corrige na mesa (E202.F3). */}
      <CorrigirNaMesa caseId={caseId} saida={saida} oculto={linha.final} />
      {falha === 'atualizando' ? <AvisoDeAtualizacao saida={saida} /> : null}
      {falha === 'erro' ? (
        <p role="alert" className="text-body-sm text-danger-700">
          {t.erroAoBaixar}
        </p>
      ) : null}
    </div>
  );
}

/** "Retomar" só na saída que falhou, com o formulário do motivo. */
function RetomarASaida({
  saida,
  onRetomar,
}: Pick<AcoesDaSaidaProps, 'saida' | 'onRetomar'>): JSX.Element {
  const [retomando, setRetomando] = useState(false);

  async function retomar(justificativa: string): Promise<DesfechoDaRetomada> {
    const desfecho = await onRetomar(saida, justificativa);
    if (desfecho.aceita) setRetomando(false);
    return desfecho;
  }

  if (retomando) {
    return (
      <RetomadaDaSaida saida={saida} onRetomar={retomar} onCancelar={() => setRetomando(false)} />
    );
  }
  return (
    <Button
      type="button"
      size="sm"
      onClick={() => setRetomando(true)}
      data-testid={`pacote-retomar-${saida}`}
    >
      {t.retomar}
    </Button>
  );
}

/** A prévia pronta para baixar, a que está sendo refeita e a retomada da saída que falhou. */
export function AcoesDaSaida(props: AcoesDaSaidaProps): JSX.Element | null {
  const { saida, linha } = props;
  if (emAtualizacao(linha)) return <EmAtualizacao saida={saida} />;
  // E170.F2: o arquivo gerado se consulta também bloqueado ou sem conferência.
  if (linha.artefato_id != null) return <BaixarASaida {...props} />;
  if (!linha.retomavel) return null;
  return <RetomarASaida saida={saida} onRetomar={props.onRetomar} />;
}
